/**
 * One recommendation round in detail.
 *
 * The first choice is the large card and the alternates sit next to each other
 * below it. Nothing here explains a candidate in prose: the expandable panel
 * shows the structured numbers only.
 *
 * A round is flagged as degraded when the engine asked for a human check, when
 * the score came from the local fallback rather than the decision model, or when
 * the plan had to drop an activity to fit the group. The first two are about the
 * scoring process, the third is about the plan itself, and they are shown
 * separately so a trustworthy ranking is never confused with a complete one. Only
 * the plan notice uses the word "degraded"; the scoring notice asks for a human
 * check and says which of the two reasons applies.
 */

import { useState } from 'react'
import { Link, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { isOfflineProviderName, usesTianditu } from '@shared/provider-names'
import { parsePositiveInteger } from '@shared/validation'

import { PageHeader } from '../../components/layout/page-header'
import { ErrorAlert, ErrorBlock, SkeletonCard } from '../../components/layout/states'
import { RecommendationCard } from '../../components/recommend/recommendation-card'
import { droppedActivityCauses } from '../../components/recommend/recommendation-details'
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert'
import { Button } from '../../components/ui/button'
import { EmptyState } from '../../components/ui/primitives'
import { useDecideRound } from '../../hooks/use-events'
import { useSharedRound } from '../../hooks/use-shared'
import {
  activityLabelKey,
  formatLocalShort,
  formatUnixDate,
  formatUnixSeconds,
  joinList,
} from '../../lib/format'
import { currentLocale } from '../../lib/i18n'
import { mutationErrorText } from '../../lib/mutation-error'
import { useSharedOutlet } from './shared-layout'

export function RoundDetailPage() {
  const { t } = useTranslation()
  const { shareToken, event, viewer } = useSharedOutlet()
  const round = parsePositiveInteger(useParams().round)
  const roundQuery = useSharedRound(shareToken, round)
  const decide = useDecideRound(event.id, round ?? 0)
  const [decidedId, setDecidedId] = useState<string | null>(null)

  const locale = currentLocale()

  // A segment that is not a round cannot be fetched: without this the query stays
  // disabled and the page would render its skeleton forever.
  if (!round) {
    return (
      <div className="flex flex-col gap-3">
        <EmptyState title={t('common.notFound')} description={t('error.not_found')} />
      </div>
    )
  }

  if (roundQuery.isPending) {
    return (
      <div className="flex flex-col gap-3">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    )
  }

  if (roundQuery.isError || !roundQuery.data) {
    return (
      <ErrorBlock
        title={t('error.not_found')}
        description={t('common.notFound')}
        onRetry={() => void roundQuery.refetch()}
        retryLabel={t('common.retry')}
      />
    )
  }

  const detail = roundQuery.data.round

  // A round exists, and is pollable, from the moment it is queued; until it is
  // ready there is nothing to show but the step it is on.
  if (detail.runStatus !== 'ready' && detail.runStatus !== 'failed') {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader
          breadcrumbs={[
            { label: t('nav.home'), to: '/' },
            { label: event.title, to: `/e/${shareToken}` },
            { label: t('recommend.title'), to: `/e/${shareToken}/recommendations` },
            { label: t('recommend.roundLabel', { round: detail.round }) },
          ]}
          title={t('recommend.roundLabel', { round: detail.round })}
        />
        <Alert variant="info">
          <AlertDescription>
            {t('round.progress.title')}
            {detail.progress ? ` ${t(detail.progress.label, { done: detail.progress.done, total: detail.progress.total })}` : ''}
            {' '}
            {t('round.progress.leaveHint')}
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  // Both notices describe the result the round proposes, so they follow the first
  // choice. A single alternate the model failed to answer used to make the whole
  // round read as locally scored; its own card still carries the fallback badge.
  const fallbackScored = detail.primary?.details.scoring.source === 'fallback'
  const degraded = detail.needsReview || fallbackScored
  // The plan alert names each dropped activity's cause, read from the same facts
  // the details panel uses so the two cannot disagree.
  const planDropCauses = detail.primary
    ? droppedActivityCauses(detail.primary.details)
    : { withoutPlaces: [], withoutTime: [] }

  /** Locking a candidate in is an organizer action, and only while open. */
  const canDecide = viewer.isOrganizer && detail.status === 'open' && event.status !== 'archived'
  const decidedRecommendationId = decidedId ?? detail.decidedRecommendationId

  function finalize(recommendationId: string): void {
    // `useDecideRound` invalidates the share-link views itself, so the page only
    // has to remember what it just decided.
    decide.mutate(recommendationId, {
      onSuccess: () => setDecidedId(recommendationId),
    })
  }

  return (
    <div className="flex flex-col gap-4">
      {detail.runStatus === 'failed' ? (
        <Alert variant="warning">
          <AlertDescription>
            {t('round.status.failed', { reason: detail.failureReason ?? 'internal' })}
          </AlertDescription>
        </Alert>
      ) : null}
      {detail.dataDegraded !== 'none' ? (
        <Alert variant="warning">
          <AlertDescription>{t(`round.degraded.${detail.dataDegraded}`)}</AlertDescription>
        </Alert>
      ) : null}
      <PageHeader
        breadcrumbs={[
          { label: t('nav.home'), to: '/' },
          { label: event.title, to: `/e/${shareToken}` },
          { label: t('recommend.title'), to: `/e/${shareToken}/recommendations` },
          { label: t('recommend.roundLabel', { round: detail.round }) },
        ]}
        title={t('recommend.roundLabel', { round: detail.round })}
        description={t('recommend.createdAt', {
          date: formatUnixSeconds(detail.createdAt, locale),
        })}
      />

      <p className="text-muted-foreground text-xs">
        {t('recommend.voteCount', { count: detail.voteCount })}
        {' · '}
        {t('recommend.peopleAndUnits', {
          people: t('recommend.participantCount', { count: detail.peopleCount }),
          units: t('recommend.unitCount', { count: detail.unitCount }),
        })}
        {detail.status === 'decided' ? ` · ${t('recommend.decided')}` : ''}
      </p>

      {/* The same sentences used to live only in `title` tooltips, which a touch
          screen cannot show, so the scale is stated where the numbers are read. */}
      {detail.primary ? (
        <p className="text-muted-foreground text-xs">{t('recommend.scoreLegend')}</p>
      ) : null}

      {detail.dataFreshness.length > 0 ? (
        <section className="flex flex-col gap-0.5">
          <h4 className="text-xs font-medium">{t('recommend.freshnessTitle')}</h4>
          <ul className="text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
            {detail.dataFreshness.map((entry) => (
              <li key={entry.key}>
                {t(`reference.${entry.key}`)}
                {' · '}
                {entry.lastRefreshedAt === null
                  ? t('recommend.freshnessNever')
                  : t('recommend.freshnessRefreshed', {
                      date: formatLocalShort(formatUnixDate(entry.lastRefreshedAt), locale),
                    })}
              </li>
            ))}
          </ul>
          {/* The dates are dataset refresh times, not this round's checks. Without
              saying so, a stale date and a "not checked" verdict read as the same
              statement. */}
          <p className="text-muted-foreground text-xs">{t('recommend.freshnessHint')}</p>
        </section>
      ) : null}

      {isOfflineProviderName(detail.mapProvider) ? (
        <Alert variant="warning">
          <AlertTitle>{t('recommend.offlineTitle')}</AlertTitle>
          <AlertDescription>{t('recommend.offlineBody')}</AlertDescription>
        </Alert>
      ) : null}

      {/*
        Tianditu's licence requires a credit wherever its content is shown, so
        the note follows the recorded provider list rather than a build flag.
      */}
      {usesTianditu(detail.mapProvider) ? (
        <p className="text-muted-foreground text-xs">
          {t('recommend.tiandituCredit')}{' '}
          {/* The credit has to stay reachable: a bare 15px tall link is below the
              minimum touch target, so the anchor carries its own padding. */}
          <a
            className="inline-flex min-h-6 items-center px-1 underline"
            href="https://www.tianditu.gov.cn/"
            rel="noreferrer"
            target="_blank"
          >
            www.tianditu.gov.cn
          </a>
        </p>
      ) : null}

      {decide.error ? (
        <ErrorAlert message={mutationErrorText(decide.error, t)} />
      ) : null}

      {/*
        A degraded plan is a different fact from a degraded score: the ranking is
        trustworthy, but the round could not deliver every activity that was
        declared. It is stated before the cards so nobody reads a partial plan as
        a complete one.
      */}
      {detail.degraded && detail.primary ? (
        <Alert variant="warning">
          <AlertTitle>{t('recommend.planDegradedTitle')}</AlertTitle>
          <AlertDescription>
            {t('recommend.planDegradedBody', {
              dropped: joinList(
                detail.primary.details.plan.droppedActivities.map((activity) =>
                  t(activityLabelKey(activity)),
                ),
                t,
              ),
            })}
          </AlertDescription>
          {/* Why, not just what: the organizer can only act on "the window is too
              short" or "the district has no place for it", not on "something was
              dropped". */}
          {planDropCauses.withoutTime.length > 0 ? (
            <AlertDescription>
              {t('recommend.planDroppedNoTime', {
                activities: joinList(
                  planDropCauses.withoutTime.map((activity) => t(activityLabelKey(activity))),
                  t,
                ),
              })}
            </AlertDescription>
          ) : null}
          {planDropCauses.withoutPlaces.length > 0 ? (
            <AlertDescription>
              {t('recommend.planDroppedNoPlaces', {
                activities: joinList(
                  planDropCauses.withoutPlaces.map((activity) => t(activityLabelKey(activity))),
                  t,
                ),
              })}
            </AlertDescription>
          ) : null}
        </Alert>
      ) : null}

      {/* Two different facts used to share the word "degraded" and sit next to each
          other, which read as one condition reported twice. The plan notice keeps
          the word; this one is about the scoring process and says so. */}
      {degraded ? (
        <Alert variant="warning">
          <AlertTitle>{t('recommend.reviewTitle')}</AlertTitle>
          {detail.needsReview ? <AlertDescription>{t('recommend.needsReview')}</AlertDescription> : null}
          {fallbackScored ? (
            <AlertDescription>{t('recommend.degradedFallback')}</AlertDescription>
          ) : null}
        </Alert>
      ) : null}

      {!detail.primary ? (
        <EmptyState title={t('recommend.noRounds')} description={t('error.no_candidates')} />
      ) : (
        <RecommendationCard
          recommendation={detail.primary}
          variant="primary"
          decided={decidedRecommendationId === detail.primary.id}
          {...(canDecide ? { onDecide: () => finalize(detail.primary!.id), deciding: decide.isPending } : {})}
        />
      )}

      {detail.alternates.length > 0 ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {detail.alternates.map((alternate, index) => (
            <RecommendationCard
              key={alternate.id}
              recommendation={alternate}
              variant="alternate"
              alternateIndex={index + 1}
              decided={decidedRecommendationId === alternate.id}
              {...(canDecide ? { onDecide: () => finalize(alternate.id), deciding: decide.isPending } : {})}
            />
          ))}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-3">
        <Button asChild>
          <Link to={`/e/${shareToken}/vote/${detail.round}`}>{t('vote.title')}</Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to={`/e/${shareToken}/recommendations`}>{t('recommend.title')}</Link>
        </Button>
      </div>
    </div>
  )
}
